import { BaseShader } from "webgl-framework";
import { ShaderInputs } from "./ShaderInputs";
import { IAoShader } from "./IAoShader";
import { engine } from "../KotlinLib";

/**
 * Main shader of the imported scene: diffuse texture, half-Lambert directional
 * lighting, screen-space AO and linear fog.
 *
 * Order of operations:
 *  1. diffuse color is sampled from the material texture,
 *  2. ambient occlusion from the SSAO texture is applied to it,
 *  3. half-Lambert directional lighting: ambientColor + pow(nDotL * 0.5 + 0.5, exponent),
 *  4. linear fog is mixed in last.
 *
 * The light direction points towards the light; normals are brought into world
 * space by the model matrix (the merged group meshes already store world-space
 * normals and are drawn with an identity transform).
 *
 * Uniforms: [view_proj_matrix, model_matrix, sTexture, lightParams, ambientColor,
 * fogColor, fogStartDistance, fogDistance]. `aoTexture` and `inverseScreenSize` are
 * bound by the renderer, see [IAoShader].
 */
export class DiffuseHalfLambertAoShader extends BaseShader implements ShaderInputs, IAoShader {
    view_proj_matrix: WebGLUniformLocation | undefined;
    model_matrix: WebGLUniformLocation | undefined;
    sTexture: WebGLUniformLocation | undefined;
    lightParams: WebGLUniformLocation | undefined;
    ambientColor: WebGLUniformLocation | undefined;
    fogColor: WebGLUniformLocation | undefined;
    fogStartDistance: WebGLUniformLocation | undefined;
    fogDistance: WebGLUniformLocation | undefined;
    aoTexture: WebGLUniformLocation | undefined;
    inverseScreenSize: WebGLUniformLocation | undefined;

    rm_Vertex: number | undefined;
    rm_TexCoord0: number | undefined;
    rm_Normal: number | undefined;

    public attributes?: number[];
    public typedAttributes?: { [key: number]: number };
    public uniforms?: WebGLUniformLocation[];

    /** @inheritdoc */
    fillCode() {
        // The fog amount is computed per vertex like in the reference implementation.
        this.vertexShaderCode = `precision highp float;
            uniform mat4 view_proj_matrix;
            uniform mat4 model_matrix;
            uniform float fogStartDistance;
            uniform float fogDistance;
            attribute vec4 rm_Vertex;
            attribute vec2 rm_TexCoord0;
            attribute vec3 rm_Normal;
            varying vec2 vTextureCoord;
            varying vec3 vWorldNormal;
            varying float vFogAmount;

            void main() {
              gl_Position = view_proj_matrix * rm_Vertex;
              vTextureCoord = rm_TexCoord0;
              vWorldNormal = (model_matrix * vec4(rm_Normal, 0.0)).xyz;
              vFogAmount = clamp((length(gl_Position) - fogStartDistance) / fogDistance, 0.0, 1.0);
            }`;

        this.fragmentShaderCode = `precision mediump float;
            varying vec2 vTextureCoord;
            varying vec3 vWorldNormal;
            varying float vFogAmount;

            uniform sampler2D sTexture;
            // x, y, z = direction towards the light, w = half-Lambert exponent.
            uniform vec4 lightParams;
            uniform vec3 ambientColor;
            uniform vec4 fogColor;

            uniform sampler2D aoTexture;
            uniform vec2 inverseScreenSize;

            void main() {
              vec4 color = texture2D(sTexture, vTextureCoord);

              color.rgb *= ambientColor;

              // Screen-space ambient occlusion, applied before the lighting.
              highp vec2 aoUV = gl_FragCoord.xy * inverseScreenSize;
              float ao = texture2D(aoTexture, aoUV).r;
              color.rgb *= ao;

              // Half-Lambert directional lighting plus the ambient light, which is what
              // keeps the surfaces facing away from the light from going black.
              vec3 lightDirection = normalize(lightParams.xyz);
              float nDotL = max(dot(normalize(vWorldNormal), lightDirection), 0.0);
              float halfLambert = pow(nDotL * 0.5 + 0.5, lightParams.w);
              color.rgb *= halfLambert;

              // Linear fog, applied last.
              gl_FragColor = mix(color, fogColor, vFogAmount);
            }`;
    }

    /** @inheritdoc */
    fillUniformsAttributes() {
        this.rm_Vertex = this.getAttrib("rm_Vertex");
        this.rm_TexCoord0 = this.getAttrib("rm_TexCoord0");
        this.rm_Normal = this.getAttrib("rm_Normal");

        this.view_proj_matrix = this.getUniform("view_proj_matrix");
        this.model_matrix = this.getUniform("model_matrix");
        this.sTexture = this.getUniform("sTexture");
        this.lightParams = this.getUniform("lightParams");
        this.ambientColor = this.getUniform("ambientColor");
        this.fogColor = this.getUniform("fogColor");
        this.fogStartDistance = this.getUniform("fogStartDistance");
        this.fogDistance = this.getUniform("fogDistance");

        this.aoTexture = this.getUniform("aoTexture");
        this.inverseScreenSize = this.getUniform("inverseScreenSize");

        this.attributes = [this.rm_Vertex, this.rm_TexCoord0, this.rm_Normal];
        this.typedAttributes = {
            [engine.AttributeType.VERTEX.ordinal]: this.rm_Vertex!,
            [engine.AttributeType.UV0.ordinal]: this.rm_TexCoord0!,
            [engine.AttributeType.NORMAL.ordinal]: this.rm_Normal!
        };
        this.uniforms = [
            this.view_proj_matrix,
            this.model_matrix,
            this.sTexture,
            this.lightParams,
            this.ambientColor,
            this.fogColor,
            this.fogStartDistance,
            this.fogDistance,
            this.aoTexture
        ];
    }
}
